import React, { useEffect, useState } from "react";
import { api, fmtDate, ROLE_LABELS } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { UserCheck, UserX, ShieldCheck, Mail, Phone, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ADMIN_ROLES = new Set(["super_admin", "owner_director", "operations_admin"]);

export default function Approvals() {
  const { user } = useAuth();
  const t = useT();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null);
  const [working, setWorking] = useState(null);

  const reload = async () => {
    try {
      const { data } = await api.get("/users/pending");
      setPending(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load pending users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && ADMIN_ROLES.has(user.role)) reload();
    else setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (user && !ADMIN_ROLES.has(user.role)) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const approve = async (u, role) => {
    setWorking(u.id);
    try {
      await api.post(`/users/${u.id}/approval`, { action: "approve", role });
      toast.success(t("approvals.approved", { name: u.name }));
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    } finally {
      setWorking(null);
    }
  };

  const reject = async (u) => {
    setWorking(u.id);
    try {
      await api.post(`/users/${u.id}/approval`, { action: "reject" });
      toast.success(t("approvals.rejected"));
      setRejecting(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    } finally {
      setWorking(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div>
        <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("approvals.subtitle")}</div>
        <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
          {t("approvals.title")}
          {pending.length > 0 && (
            <span className="badge-pill badge-pending" data-testid="pending-count">{pending.length}</span>
          )}
        </h2>
        <p className="text-sm text-[#94A3B8] mt-1">{t("approvals.intro")}</p>
      </div>

      {loading ? (
        <div className="h-64 ledger-card animate-pulse" />
      ) : pending.length === 0 ? (
        <div className="ledger-card p-12 text-center text-[#94A3B8]">
          <ShieldCheck className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <div className="text-sm">{t("approvals.empty")}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {pending.map(u => (
            <PendingRow
              key={u.id} u={u} t={t}
              working={working === u.id}
              rejecting={rejecting === u.id}
              onApprove={approve}
              onRequestReject={() => setRejecting(u.id)}
              onConfirmReject={() => reject(u)}
              onCancelReject={() => setRejecting(null)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PendingRow({ u, t, working, rejecting, onApprove, onRequestReject, onConfirmReject, onCancelReject }) {
  const [role, setRole] = useState(u.role || "skipper");
  return (
    <div className="ledger-card p-5" data-testid={`pending-row-${u.id}`}>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-lg font-bold tracking-tight">{u.name}</div>
            <span className="badge-pill badge-pending">{t("approvals.pendingBadge")}</span>
          </div>
          <div className="text-xs text-[#94A3B8] font-mono flex items-center gap-1 flex-wrap">
            <Mail className="w-3 h-3" /> {u.email}
            {u.phone && (<><span>·</span><Phone className="w-3 h-3" /> {u.phone}</>)}
          </div>
          <div className="text-[11px] text-[#94A3B8] font-mono flex items-center gap-1">
            <Clock className="w-3 h-3" /> {t("approvals.requested", { date: fmtDate(u.created_at) })}
          </div>
        </div>
        <div className="flex flex-col gap-2 min-w-[260px]">
          <div>
            <div className="text-[10px] font-mono tracking-widest text-[#94A3B8] mb-1">{t("approvals.role")}</div>
            <select
              value={role} onChange={(e) => setRole(e.target.value)}
              data-testid={`role-select-${u.id}`}
              className="w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm"
            >
              {Object.keys(ROLE_LABELS).map(k => (
                <option key={k} value={k}>{t(`role.${k}`)}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => onApprove(u, role)} disabled={working}
              data-testid={`approve-btn-${u.id}`}
              className="flex-1 bg-[#10B981] hover:bg-[#059669]"
            >
              <UserCheck className="w-4 h-4 mr-1" /> {t("approvals.approve")}
            </Button>
            <Button
              onClick={onRequestReject} disabled={working}
              data-testid={`reject-btn-${u.id}`}
              className="flex-1 bg-[#0B1221] border border-[#EF4444]/50 hover:bg-[#EF4444]/10 text-[#F87171]"
            >
              <UserX className="w-4 h-4 mr-1" /> {t("approvals.reject")}
            </Button>
          </div>
        </div>
      </div>
      {rejecting && (
        <div className="mt-4 bg-[#EF4444]/10 border border-[#EF4444]/40 rounded-md p-3" data-testid={`confirm-reject-${u.id}`}>
          <div className="text-xs text-[#F87171] mb-2">{t("approvals.confirmReject")}</div>
          <div className="flex gap-2">
            <Button onClick={onConfirmReject} disabled={working} data-testid={`confirm-reject-btn-${u.id}`} className="flex-1 bg-[#EF4444] hover:bg-[#DC2626]">{t("approvals.reject")}</Button>
            <Button onClick={onCancelReject} className="flex-1 bg-[#050A14] border border-[#1E293B] hover:bg-[#131C31]">{t("common.cancel")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
